import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CalendarDays, Check, CircleUser, Clock, FileText, Loader2, Repeat, Tag, Tags, Users, Video, X as XIcon } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useCreateCalendarEvent, useUpdateCalendarEvent, useRescheduleEvent, useSaveEventTags } from '../../hooks/useCalendar';
import { SCHEDULE_DISABLED_COPY, useScheduleCapabilities } from '../../hooks/useScheduleCapabilities';
import CalendarPicker from '../../components/schedule/CalendarPicker';
import ContactEmailTypeahead from '../../components/ContactEmailTypeahead';
import TagInput from '../../components/TagInput';
import TimezoneSelect from '../../components/TimezoneSelect';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { errorText } from '../../lib/apiErrors';
import { CRM_ONLY_NOTE, eventTagErrorText, TAG_ORG_WIDE_HINT } from '../../lib/tagCopy';
import { parseInstant, resolveDisplayZone } from '../../lib/timezone';
import { eventCapabilities } from '../../lib/calendar/eventCapabilities';
import { eventDayKey } from '../../lib/calendar/zonedCalendar';
import {
    buildCreatePayload, buildUpdatePayload, canEditAttendees, conferenceOptionFor, initialForm,
    nextShowAs, pickDefaultCalendar, providerFieldsChanged, repeatOptions, SHOW_AS_LABELS, shiftStart, showAsFor, validateMeetingForm,
} from './meetingForm';
import { DayTimelinePanel } from './DayTimelinePanel';
import { useCalendarEventLookup } from './useCalendarEventLookup';

const input = 'h-[34px] px-[11px] border border-[#E4E1DB] rounded-[9px] bg-white text-[13px] text-[#0C0A09] outline-none box-border focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/15';
const row = 'flex items-center gap-[13px] py-[13px] border-b border-[#F2F0EC] flex-wrap';
const rowIcon = 'w-[17px] h-[17px] flex-none text-[#8A837E]';

/**
 * Create and edit a meeting (#680, prototype `isCalNew`) at /calendar/new and
 * /calendar/event/:eventId/edit — a full surface with a day timeline beside
 * it, replacing ScheduleMeetingModal (create) and EventDetailModal's inline
 * form (edit, which used browser-local datetime inputs).
 *
 * Grouped as the prototype groups it: what (title) · who · when · how to meet
 * · notes. "Hosting calendar" (the provider, #680 D3) sits in the action bar;
 * the "Add online meeting" toggle is buildLocationOptions (on = the host's
 * conference, off = none — #513 keeps non-conference locations withdrawn).
 * Event | Series, Busy/Free/Tentative, Optional attendees and the Repeat
 * select are create-only: they reach the provider on create
 * (services/calendar_recurrence.py), and no update route takes them. Series
 * and the Repeat select are one control seen twice — Series means "repeats".
 * Tags (#680 on #682) are the "what it is for" row: TagInput, saved by a
 * SECOND call after the event (useSaveEventTags), never inside the create /
 * update payload. A tag is CRM-side only, so an edit that changes only tags
 * never PUTs the provider (providerFieldsChanged).
 * Still not built: "Scheduler" (no free/busy lookup for other people exists)
 * and the free-text location row (#513).
 */
export default function CalendarEditorPage() {
    const { eventId } = useParams();
    const editing = Boolean(eventId);
    const { user } = useAuth();
    const displayZone = resolveDisplayZone({ user });
    const lookup = useCalendarEventLookup(eventId, displayZone);

    if (editing && lookup.isLoading) {
        return <div className="flex flex-col h-full min-h-0 p-6 gap-3" aria-busy="true"><Skeleton className="h-8 w-72" /><Skeleton className="h-48 w-full" /></div>;
    }
    if (editing && !lookup.event) {
        return (
            <div className="flex flex-col h-full min-h-0 items-center justify-center gap-2 p-8 text-center" data-testid="event-not-found">
                <p className="text-[14px] font-semibold text-[#0C0A09]">This event is no longer on your calendar.</p>
                <a href="/calendar" className="text-[12.5px] font-medium text-[#4D11F6] hover:underline">Back to calendar</a>
            </div>
        );
    }
    return <MeetingEditor key={eventId || 'new'} event={editing ? lookup.event : null} displayZone={displayZone} user={user} />;
}

function MeetingEditor({ event, displayZone, user }) {
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { calendars, anyCalendarConnected } = useScheduleCapabilities();
    const createEvent = useCreateCalendarEvent();
    const updateEvent = useUpdateCalendarEvent();
    const rescheduleEvent = useRescheduleEvent();
    const saveTags = useSaveEventTags();
    const editing = Boolean(event);
    const caps = eventCapabilities(event);

    const [form, setForm] = useState(() => initialForm({
        event,
        start: parseInstant(params.get('start')),
        end: parseInstant(params.get('end')),
        zone: displayZone,
        calendar: pickDefaultCalendar(calendars, null),
    }));
    const initial = useRef(form);
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const [showOptional, setShowOptional] = useState(false);
    const titleRef = useRef(null);
    const optionalRef = useRef(null);
    useEffect(() => { titleRef.current?.focus(); }, []);

    // The event can be replaced after mount (router-state copy → the ?day=
    // refetch, or a tag change made elsewhere). Until the user touches the Tags
    // field, follow the event's current tags — so Save never reverts a tag set
    // somewhere else — and keep `initial` in step so that is not a "change".
    const tagsTouched = useRef(false);
    const eventTagNames = (event?.tags || []).filter((t) => t?.name).map((t) => t.name);
    const eventTagKey = eventTagNames.join('\u0000');
    useEffect(() => {
        if (!editing || tagsTouched.current) return;
        initial.current = { ...initial.current, tags: eventTagNames };
        setForm((f) => ({ ...f, tags: eventTagNames }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventTagKey, editing]);
    useEffect(() => { if (showOptional) optionalRef.current?.querySelector('input')?.focus(); }, [showOptional]);

    // Re-pick the host as connections resolve (ScheduleMeetingModal's rule).
    useEffect(() => {
        if (editing) return;
        setForm((f) => {
            const calendar = pickDefaultCalendar(calendars, f.calendar);
            return { ...f, calendar, showAs: showAsFor(calendar, f.showAs) };
        });
    }, [calendars, editing]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const dirty = JSON.stringify(form) !== JSON.stringify(initial.current);
    const conference = conferenceOptionFor(form.calendar);
    const saving = createEvent.isPending || updateEvent.isPending || rescheduleEvent.isPending || saveTags.isPending;
    const attendeesEditable = canEditAttendees(event);

    const leave = (day) => navigate('/calendar', { state: { anchorDay: day || form.date || undefined } });
    const discard = () => (dirty ? setConfirmDiscard(true) : leave(event ? eventDayKey(event, displayZone) : undefined));

    const save = async () => {
        const problem = validateMeetingForm(form);
        if (problem) { toast.error(problem); return; }
        // Only a change the provider stores reaches the provider (tags are CRM-side).
        const providerChange = !editing || providerFieldsChanged(form, initial.current);
        let tagTarget = editing ? event.event_id : null;
        try {
            if (editing && !providerChange) {
                // Tags only (or nothing): no PUT, no reschedule.
            } else if (editing) {
                const payload = buildUpdatePayload(event, form, initial.current);
                if (event.source === 'coach_booking' && payload.start) {
                    // A coach booking reschedules through the booking service.
                    await rescheduleEvent.mutateAsync({ eventId: event.event_id, start: payload.start, end: payload.end });
                    if (form.title !== initial.current.title || 'description' in payload) {
                        const details = { eventId: payload.eventId, source: payload.source, title: form.title };
                        if ('description' in payload) details.description = payload.description;
                        await updateEvent.mutateAsync(details);
                    }
                } else {
                    await updateEvent.mutateAsync(payload);
                }
                // "Event updated" is said once, after the tags (below).
            } else {
                // Success / partial-success toasts belong to useCreateCalendarEvent.
                const res = await createEvent.mutateAsync(buildCreatePayload(form));
                // The CRM stub is always written first, and its tags follow its
                // provider copies and occurrences (#682 anchors).
                const crmId = res?.data?.crm_event_id;
                tagTarget = crmId ? `crm_${crmId}` : null;
            }
        } catch (err) {
            // The Teams-refusal 422 (#514 D1) carries copy the user can act on.
            toast.error(errorText(err, editing ? 'Failed to update event' : 'Failed to schedule meeting'));
            return;
        }

        // Tags: a second call. When the event itself saved, a tag failure is a
        // warning and the editor still closes — the meeting is not rolled back.
        let failure = null;
        let changed = false;
        if (form.tags.length || (editing && (event.tags || []).length)) {
            if (!tagTarget) {
                failure = eventTagErrorText(null);
            } else {
                try {
                    const result = await saveTags.mutateAsync({
                        eventId: tagTarget, before: editing ? event.tags || [] : [], afterNames: form.tags,
                    });
                    changed = result.changed;
                    if (result.failed.length) failure = eventTagErrorText(result.failed[0].reason);
                } catch (err) {
                    failure = eventTagErrorText(err);
                }
            }
        }
        if (failure && !providerChange) {
            // Nothing else was saved: stay, so the user can try again.
            toast.error(`Tags weren't saved: ${failure}`);
            return;
        }
        // One message per outcome. A create's "Meeting scheduled" toast is
        // useCreateCalendarEvent's, so the create warning only adds what it didn't say.
        if (failure && editing) toast.warning(`Event updated, but its tags weren't saved: ${failure}`);
        else if (failure) toast.warning(`Its tags weren't saved: ${failure}. Add them from the event.`);
        else if (editing && providerChange) toast.success('Event updated');
        else if (editing && changed) toast.success('Tags updated');
        leave(form.date);
    };

    const ownAddress = user?.email || '';
    const heading = editing ? 'Edit event' : 'New event';
    const hostOptions = useMemo(() => {
        if (!editing) return calendars;
        const host = form.calendar || event.source;
        const match = calendars.find(c => c.value === host);
        return match ? [match] : [];
    }, [editing, calendars, form.calendar, event]);

    return (
        <div className="flex flex-col h-full min-h-0 bg-white" data-testid="schedule-meeting-modal">
            <div className="flex flex-none items-center gap-2.5 px-[18px] py-[11px] border-b border-[#EDEBE6]">
                <CalendarDays className="w-4 h-4 text-[#57504B]" />
                <h1 className="flex-1 min-w-0 text-[13.5px] text-[#57504B] font-normal">{heading}</h1>
                <button type="button" onClick={discard} title="Discard" aria-label="Discard" className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-[#57504B] hover:bg-[#F4F2EE]">
                    <XIcon className="w-[17px] h-[17px]" />
                </button>
            </div>
            <div className="flex flex-none flex-wrap items-center gap-2 px-[18px] py-2.5 border-b border-[#EDEBE6]">
                {!editing && (
                    <>
                        <div role="group" aria-label="Event or series" className="inline-flex gap-2">
                            {[
                                { key: 'event', label: 'Event', Icon: CalendarDays, on: form.repeat === 'none', pick: () => set({ repeat: 'none' }) },
                                { key: 'series', label: 'Series', Icon: Repeat, on: form.repeat !== 'none', pick: () => set({ repeat: form.repeat !== 'none' ? form.repeat : form.date ? 'weekly' : 'weekdays' }) },
                            ].map(({ key, label, Icon, on, pick }) => (
                                <button
                                    key={key}
                                    type="button"
                                    aria-pressed={on}
                                    onClick={pick}
                                    className={`inline-flex items-center gap-[7px] h-8 px-[13px] rounded-[9px] border text-[12.5px] ${on ? 'border-[#4D11F6] bg-[#EFEAFE] text-[#3B0DBE] font-semibold' : 'border-[#E4E1DB] bg-white text-[#3A3532] font-medium hover:bg-[#F4F2EE]'}`}
                                    data-testid={`meeting-kind-${key}`}
                                >
                                    <Icon className="w-3.5 h-3.5" /> {label}
                                </button>
                            ))}
                        </div>
                        <div className="w-px h-[22px] bg-[#EDEBE6] mx-[3px]" aria-hidden="true" />
                        <button
                            type="button"
                            onClick={() => set({ showAs: nextShowAs(form.showAs, form.calendar) })}
                            aria-label={`Show as ${SHOW_AS_LABELS[form.showAs]} — click to change`}
                            title="Show as"
                            className="inline-flex items-center gap-[7px] h-8 px-3 border border-[#E4E1DB] rounded-[9px] bg-white text-[12.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]"
                            data-testid="meeting-show-as"
                        >
                            <CircleUser className="w-3.5 h-3.5 text-[#57504B]" /> {SHOW_AS_LABELS[form.showAs]}
                        </button>
                    </>
                )}
                {hostOptions.length > 0 ? (
                    <CalendarPicker
                        options={hostOptions}
                        value={form.calendar}
                        onChange={(value) => set({ calendar: value, showAs: showAsFor(value, form.showAs) })}
                        layout="inline"
                        disabled={editing}
                    />
                ) : (
                    !editing && <span className="text-[12.5px] text-[#A3382B]">{SCHEDULE_DISABLED_COPY}</span>
                )}
                <div className="flex-1" />
                <button type="button" onClick={discard} className="inline-flex items-center h-8 px-[13px] border border-[#E4E1DB] rounded-[9px] bg-white text-[12.5px] font-medium text-[#3A3532] hover:bg-[#F4F2EE]" data-testid="meeting-discard">
                    Discard
                </button>
                <button
                    type="button"
                    onClick={save}
                    disabled={saving || (!editing && !anyCalendarConnected)}
                    className="inline-flex items-center gap-[7px] h-8 px-3.5 rounded-[9px] bg-[#4D11F6] text-white text-[12.5px] font-semibold hover:bg-[#3B0DBE] disabled:opacity-60"
                    data-testid="meeting-submit"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save
                </button>
            </div>

            <div className="flex flex-1 min-h-0 items-stretch">
                <form
                    className="flex-1 min-w-0 overflow-y-auto px-6 pt-[18px] pb-12"
                    onSubmit={(e) => { e.preventDefault(); save(); }}
                    aria-label={heading}
                >
                    {caps.note && (
                        <p className="mb-3 p-2.5 rounded-[9px] bg-[#FFFBEB] border border-[#FDE68A] text-[12px] text-[#92400E]" data-testid="event-capability-note">{caps.note}</p>
                    )}
                    {/* What */}
                    <div className="flex items-center gap-[13px] pb-3 border-b border-[#F2F0EC]">
                        <Tag className={rowIcon} aria-hidden="true" />
                        <input
                            ref={titleRef}
                            value={form.title}
                            onChange={(e) => set({ title: e.target.value })}
                            placeholder="Add title"
                            aria-label="Title"
                            className="flex-1 min-w-0 border-0 outline-none bg-transparent text-[17px] font-semibold tracking-[-0.01em] text-[#0C0A09] placeholder:text-[#A29B95]"
                            data-testid="meeting-title"
                        />
                    </div>

                    {/* What it is for — tags (#680). A "calendar" in My calendars IS a tag.
                        Directly under the title, so the suggestion list opens over the
                        form rather than past the bottom of the scroll column. */}
                    <div className="flex items-start gap-[13px] py-[13px] border-b border-[#F2F0EC]" data-testid="meeting-tags">
                        <Tags className={`${rowIcon} mt-2.5`} aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                            <TagInput
                                value={form.tags}
                                onChange={(tags) => { tagsTouched.current = true; set({ tags }); }}
                                placeholder="Add tags — e.g. Work, Personal, Client calls"
                                ariaLabel="Tags"
                                createHint={TAG_ORG_WIDE_HINT}
                            />
                            <p className="mt-1.5 text-[11.5px] text-[#A29B95]" data-testid="meeting-tags-note">
                                {CRM_ONLY_NOTE}{caps.tagNote ? ` ${caps.tagNote}` : ''}
                            </p>
                        </div>
                    </div>

                    {/* Who */}
                    <div className={row} data-testid="meeting-attendees">
                        <Users className={rowIcon} aria-hidden="true" />
                        {attendeesEditable ? (
                            <>
                                <ContactEmailTypeahead
                                    value={form.attendees}
                                    onChange={(attendees) => set({ attendees })}
                                    placeholder={editing ? 'Invite attendees — search contacts or type an email' : 'Invite required attendees'}
                                    variant="bare"
                                    suggestOnFocus
                                    ownDomain={ownAddress}
                                />
                                {!editing && !showOptional && (
                                    <button
                                        type="button"
                                        onClick={() => setShowOptional(true)}
                                        className="flex-none text-[12.5px] font-medium text-[#4D11F6] hover:underline"
                                        data-testid="meeting-optional-link"
                                    >
                                        Optional
                                    </button>
                                )}
                            </>
                        ) : (
                            <span className="text-[13px] text-[#57504B]">
                                {form.attendees.length ? form.attendees.map(a => a.name || a.email).join(', ') : 'No attendees'}
                                <span className="block text-[11.5px] text-[#A29B95]">Attendees of this event are managed where it was booked.</span>
                            </span>
                        )}
                    </div>

                    {!editing && showOptional && (
                        <div className={row} ref={optionalRef} data-testid="meeting-optional-attendees">
                            <span className="w-[17px] flex-none" aria-hidden="true" />
                            <ContactEmailTypeahead
                                value={form.optionalAttendees}
                                onChange={(optionalAttendees) => set({ optionalAttendees })}
                                placeholder="Invite optional attendees"
                                variant="bare"
                                suggestOnFocus
                                ownDomain={ownAddress}
                            />
                        </div>
                    )}

                    {/* When */}
                    {editing && event.all_day ? (
                        <div className={row} data-testid="meeting-all-day-readonly">
                            <Clock className={rowIcon} aria-hidden="true" />
                            <span className="text-[13.5px] text-[#0C0A09]">All day · {form.date}</span>
                            <span className="basis-full pl-[30px] text-[11.5px] text-[#A29B95]">The dates of an all-day event are changed in the calendar that hosts it.</span>
                        </div>
                    ) : (
                    <div className={row}>
                        <Clock className={rowIcon} aria-hidden="true" />
                        <input type="date" value={form.date} onChange={(e) => set({ date: e.target.value })} aria-label="Date" className={`${input} w-[160px]`} data-testid="meeting-date" />
                        {!form.allDay && (
                            <>
                                <input type="time" value={form.startTime} onChange={(e) => setForm(f => shiftStart(f, e.target.value))} aria-label="Start time" className={`${input} w-[112px]`} data-testid="meeting-start" />
                                <span className="text-[13px] text-[#8A837E]">to</span>
                                <input type="time" value={form.endTime} onChange={(e) => set({ endTime: e.target.value })} aria-label="End time" className={`${input} w-[112px]`} data-testid="meeting-end" />
                            </>
                        )}
                        <label className="inline-flex items-center gap-2 cursor-pointer text-[13px] text-[#3A3532]">
                            <input type="checkbox" checked={form.allDay} onChange={(e) => set({ allDay: e.target.checked })} className="w-4 h-4 accent-[#4D11F6]" data-testid="meeting-all-day" />
                            All day
                        </label>
                        <div className="basis-full h-0" />
                        <span className="w-[17px] flex-none" aria-hidden="true" />
                        {/* The zone the wall clock above is authored IN (#540): changing it
                            reinterprets the time, it does not renumber it. */}
                        <div className="w-[280px] max-w-full">
                            <TimezoneSelect id="calendar-editor-timezone" value={form.timezone} onChange={(timezone) => set({ timezone })} ariaLabel="Timezone" />
                        </div>
                    </div>
                    )}

                    {/* Repeat — create only (Series in the action bar is the same choice). */}
                    {!editing && (
                        <div className={row}>
                            <Repeat className={rowIcon} aria-hidden="true" />
                            <select
                                value={form.repeat}
                                onChange={(e) => set({ repeat: e.target.value })}
                                aria-label="Repeat"
                                className={`${input} pr-8`}
                                data-testid="meeting-repeat"
                            >
                                {repeatOptions(form.date).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>
                    )}

                    {/* How to meet */}
                    <div className="flex items-center gap-[13px] py-[13px] border-b border-[#F2F0EC]">
                        <Video className={rowIcon} aria-hidden="true" />
                        {editing ? (
                            <span className="flex-1 text-[13.5px] text-[#57504B]">
                                {event.conference_link ? 'Online meeting link attached' : 'No online meeting'}
                                <span className="block text-[11.5px] text-[#A29B95]">An online meeting is added when an event is created; it can't be added or removed here.</span>
                            </span>
                        ) : (
                            <>
                                <span className="flex-1 text-[13.5px] text-[#0C0A09]">
                                    {conference ? `Add ${conference.label.replace(' (auto-link)', '')}` : 'Add online meeting'}
                                    {!conference && <span className="block text-[11.5px] text-[#A29B95]">Choose a hosting calendar first.</span>}
                                </span>
                                <button
                                    type="button"
                                    role="switch"
                                    aria-checked={Boolean(conference && form.online)}
                                    aria-label="Add online meeting"
                                    disabled={!conference}
                                    onClick={() => set({ online: !form.online })}
                                    className={`flex-none w-[38px] h-[22px] rounded-full p-0.5 box-border flex items-center transition-colors ${conference && form.online ? 'bg-[#4D11F6]' : 'bg-[#D9D4CD]'} disabled:opacity-50`}
                                    data-testid="meeting-online-toggle"
                                >
                                    <span className={`w-[18px] h-[18px] rounded-full bg-white shadow-[0_1px_2px_rgba(12,10,9,.2)] transition-transform ${conference && form.online ? 'translate-x-4' : ''}`} />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Notes */}
                    <div className="flex items-start gap-[13px] pt-[15px]">
                        <FileText className={`${rowIcon} mt-1.5`} aria-hidden="true" />
                        <textarea
                            value={form.description}
                            onChange={(e) => set({ description: e.target.value })}
                            placeholder="Add an agenda or notes people should read before the meeting"
                            aria-label="Agenda or notes"
                            className="flex-1 min-w-0 min-h-[132px] px-3 py-2.5 border border-[#E4E1DB] rounded-[10px] bg-white text-[13.5px] text-[#0C0A09] outline-none resize-y box-border focus:border-[#4D11F6] focus:ring-2 focus:ring-[#4D11F6]/15"
                            data-testid="meeting-description"
                        />
                    </div>
                </form>

                <DayTimelinePanel
                    date={form.date}
                    startTime={form.startTime}
                    endTime={form.endTime}
                    allDay={form.allDay}
                    timezone={form.timezone}
                    excludeEventId={event?.event_id}
                    onPickStart={(start) => setForm(f => shiftStart({ ...f, allDay: false }, start))}
                />
            </div>

            <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard your changes?</AlertDialogTitle>
                        <AlertDialogDescription>{editing ? 'The event keeps its current details.' : 'This event has not been created.'}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep editing</AlertDialogCancel>
                        <AlertDialogAction onClick={() => leave(event ? eventDayKey(event, displayZone) : form.date)} data-testid="meeting-discard-confirm">Discard</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
